import { Component } from '@theme/component';
import { fetchConfig, debounce, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #updating = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    const { quantity, cartLine: line } = event.detail;
    if (!line || this.#updating) return;
    if (quantity === 0) return this.onLineItemRemove(line);
    this.updateQuantity({ line, quantity, action: 'change' });
    const row = this.refs.cartItemRows[line - 1];
    row?.querySelector('text-component')?.shimmer();
  }

  onLineItemRemove(line) {
    if (this.#updating) return;
    return this.updateQuantity({ line, quantity: 0, action: 'clear' });
  }

  updateQuantity(config) {
    if (this.#updating) return;
    this.#updating = true;
    const marker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    this.#disableCartItems();
    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionsToUpdate.add(item.dataset.sectionId);
    });
    const body = JSON.stringify({
      line,
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });
    cartTotal?.shimmer();
    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then(async (responseText) => {
        const cart = JSON.parse(responseText);
        resetShimmer(this);
        if (cart.errors) {
          this.#handleCartError(line, cart);
          return;
        }
        const html = cart.sections?.[this.sectionId];
        const parsed = html ? new DOMParser().parseFromString(html, 'text/html') : null;
        const count = Number.isFinite(cart.item_count)
          ? cart.item_count
          : parseInt(parsed?.querySelector('[ref="cartItemCount"]')?.textContent || '0', 10);
        this.dispatchEvent(new CartUpdateEvent({}, this.sectionId, {
          itemCount: count,
          source: 'cart-items-component',
          sections: cart.sections,
        }));
        if (html) await morphSection(this.sectionId, html);
        else await sectionRenderer.renderSection(this.sectionId, { cache: false });
      })
      .catch((error) => {
        console.error(error);
        resetShimmer(this);
        const errorText = this.refs[`cartItemError-${line}`];
        const errorContainer = this.refs[`cartItemErrorContainer-${line}`];
        if (errorText instanceof HTMLElement && errorContainer instanceof HTMLElement) {
          errorText.textContent = 'Unable to update your cart. Please refresh and try again.';
          errorContainer.classList.remove('hidden');
        }
      })
      .finally(() => {
        this.#updating = false;
        this.#enableCartItems();
        cartPerformance.measureFromMarker(marker);
      });
  }

  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  #handleCartError = (line, cart) => {
    const input = this.refs.quantitySelectors[line - 1]?.querySelector('input');
    if (input) input.value = input.defaultValue;
    const text = this.refs[`cartItemError-${line}`];
    const container = this.refs[`cartItemErrorContainer-${line}`];
    if (text instanceof HTMLElement && container instanceof HTMLElement) {
      text.textContent = typeof cart.errors === 'string' ? cart.errors : 'Unable to update this item.';
      container.classList.remove('hidden');
    }
  };

  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;
    const html = event.detail.data.sections?.[this.sectionId];
    if (html) morphSection(this.sectionId, html);
    else sectionRenderer.renderSection(this.sectionId, { cache: false });
  };

  #disableCartItems() { this.classList.add('cart-items-disabled'); }
  #enableCartItems() { this.classList.remove('cart-items-disabled'); }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
